import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, X, CreditCard, BookOpenCheck, Undo2, Link2, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { formatMoney, withRid, STATUS_STYLES } from "@/components/receivables/shared";

export function InvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [coa, setCoa] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [open, setOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null); // invoice for payment
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [populating, setPopulating] = useState(false);
  const [pay, setPay] = useState({ amount: "", date: format(new Date(), "yyyy-MM-dd"), method: "bank", memo: "", account_id: "" });
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const { isActive } = useCurrencyRail();
  const coaRevenue = useMemo(() => coa.filter((a) => a.type === "Revenue"), [coa]);

  function blank(kind = "inventory") {
    const today = format(new Date(), "yyyy-MM-dd");
    return {
      customer_id: "", date: today,
      gl_posting_date: today,
      due_date: format(new Date(Date.now() + 14 * 864e5), "yyyy-MM-dd"),
      reference_number: "", description: "",
      terms: "net30", payment_priority: "normal", on_hold: false,
      lines: [withRid({ product_id: "", description: "", quantity: 1, unit_price: 0, tax_rate: 0,
                         coa_code: "", department: "", bank_account_id: "",
                         memo: "" })],
      notes: "",
      currency: "USD",
      kind, // iter 72ai — "inventory" | "service"
    };
  }
  const load = useCallback(async () => {
    try {
      const [{ data: inv }, { data: cust }, { data: prod }, { data: c }, { data: d }, { data: w }] = await Promise.all([
        api.get("/invoices"), api.get("/customers"), api.get("/products"),
        api.get("/coa"), api.get("/categories"), api.get("/accounts"),
      ]);
      setInvoices(inv); setCustomers(cust); setProducts(prod || []);
      setCoa((c || []).filter((x) => x.is_active !== false));
      // `/categories` returns EITHER an array OR a grouped dict
      // ({expense:[], income:[], saving:[]}); normalize to a flat array.
      const flatDepts = Array.isArray(d)
        ? d
        : Object.values(d || {}).flat();
      setDepartments(flatDepts);
      setWallets(w || []);
    } catch (err) {
      console.warn("[Receivables] load invoices failed:", err?.message);
      toast.error("Couldn't load invoices");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Iter 70 — picking a product auto-fills description + unit_price (sell price)
  // so a user only ever types the qty. The product_id is what triggers the
  // stock move + COGS posting when the invoice's journal is booked.
  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    const n = [...form.lines];
    n[i] = {
      ...n[i],
      product_id: pid || "",
      description: p ? p.name : n[i].description,
      unit_price: p ? p.unit_price : n[i].unit_price,
    };
    setForm({ ...form, lines: n });
  };

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    for (const l of form.lines) {
      const s = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
      sub += s; tax += s * (parseFloat(l.tax_rate) || 0) / 100;
    }
    return { subtotal: sub, tax_total: tax, total: sub + tax };
  }, [form.lines]);

  const save = async ({ postAndNew = false } = {}) => {
    if (!form.customer_id) return toast.error("Pick a customer");
    if (!form.lines.some((l) => l.description)) return toast.error("Add at least one line");
    const payload = {
      customer_id: form.customer_id,
      date: form.date,
      due_date: form.due_date,
      gl_posting_date: form.gl_posting_date || null,
      reference_number: form.reference_number || null,
      description: form.description || null,
      terms: form.terms || null,
      payment_priority: form.payment_priority || "normal",
      on_hold: !!form.on_hold,
      lines: form.lines.filter((l) => l.description).map((l) => ({
        product_id: l.product_id || null,
        description: l.description,
        quantity: parseFloat(l.quantity) || 0,
        unit_price: parseFloat(l.unit_price) || 0,
        tax_rate: parseFloat(l.tax_rate) || 0,
        coa_code: l.coa_code || null,
        department: l.department || null,
        bank_account_id: l.bank_account_id || null,
        memo: l.memo || null,
      })),
      notes: form.notes,
      currency: form.currency || "USD",
      kind: form.kind || "inventory",
    };
    try {
      if (editing) await api.patch(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editing ? "Invoice updated" : "Invoice created");
      load();
      if (postAndNew) {
        setEditing(null);
        setForm(blank(form.kind));
      } else {
        setOpen(false);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  // iter 72an — Terms → Due date auto-compute.
  const applyTerms = (t, baseDate) => {
    if (!t || t === "custom") return null;
    const d = new Date(baseDate);
    if (isNaN(d)) return null;
    const days = { due_on_receipt: 0, net10: 10, net15: 15, net30: 30,
                   net45: 45, net60: 60, net90: 90 }[t];
    if (t === "eom") {
      const eom = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return format(eom, "yyyy-MM-dd");
    }
    if (days == null) return null;
    return format(new Date(d.getTime() + days * 864e5), "yyyy-MM-dd");
  };
  const setTerms = (t) => {
    const newDue = applyTerms(t, form.date);
    setForm({ ...form, terms: t, ...(newDue ? { due_date: newDue } : {}) });
  };
  const setDate = (d) => {
    const newDue = applyTerms(form.terms, d);
    setForm({ ...form, date: d, ...(newDue ? { due_date: newDue } : {}) });
  };

  // iter 72an — Populate lines/terms from the customer's most recent invoice.
  const populateFromLast = async () => {
    if (!form.customer_id) return toast.error("Pick a customer first");
    setPopulating(true);
    try {
      const { data } = await api.get("/invoices/populate-last",
                                       { params: { customer_id: form.customer_id } });
      setForm({
        ...form,
        terms: data.terms || form.terms,
        payment_priority: data.payment_priority || form.payment_priority,
        description: data.description || form.description,
        currency: data.currency || form.currency,
        kind: data.kind || form.kind,
        lines: (data.lines || []).map((l) => withRid({ ...l })),
      });
      toast.success("Populated from last invoice");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No prior invoice for this customer");
    } finally {
      setPopulating(false);
    }
  };

  const recordPayment = async () => {
    const amount = parseFloat(pay.amount);
    if (!amount || amount <= 0) return toast.error("Invalid amount");
    try {
      const { data } = await api.post(`/invoices/${payOpen.id}/payments`, { ...pay, amount });
      toast.success("Payment recorded");
      // Refresh the dialog with fresh invoice state so the payment list updates
      setPayOpen({ ...data, balance: data.balance });
      setPay({ amount: "", date: format(new Date(), "yyyy-MM-dd"), method: "bank", memo: "", account_id: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const [voidTarget, setVoidTarget] = useState(null); // { paymentId, amount, date }
  const [voidReason, setVoidReason] = useState("");
  const confirmVoid = async () => {
    if (!voidTarget) return;
    const reason = voidReason.trim();
    if (reason.length < 3) return toast.error("Reason must be at least 3 characters");
    try {
      const { data } = await api.post(
        `/invoices/${payOpen.id}/payments/${voidTarget.paymentId}/void`,
        { reason },
      );
      toast.success("Payment voided");
      setPayOpen({ ...data, balance: data.balance });
      setVoidTarget(null); setVoidReason("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Void failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await api.delete(`/invoices/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      customer_id: inv.customer_id,
      date: inv.date,
      due_date: inv.due_date,
      lines: inv.lines.map((l) => withRid(l)),
      notes: inv.notes || "",
      currency: inv.currency || "USD",
    });
    setOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        {/* iter 72ai — two separate flows: inventory invoice (with product
            picker) vs service invoice (no product, just CoA + Dept + Bank).
            Both open the same dialog; `form.kind` drives what's rendered. */}
        <Button data-testid="invoice-new-service-btn"
                 onClick={() => { setEditing(null); setForm(blank("service")); setOpen(true); }}
                 variant="outline" className="rounded-md h-10 px-4">
          <Sparkles className="w-4 h-4 mr-2" /> New service invoice
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invoice-new-btn" onClick={() => { setEditing(null); setForm(blank("inventory")); setOpen(true); }}
              className="rounded-md btn-brand h-10 px-4">
              <Plus className="w-4 h-4 mr-2" /> New invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editing ? "Edit invoice" : (form.kind === "service" ? "New service invoice" : "New invoice")}
                <span data-testid="invoice-kind-badge"
                       className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                         form.kind === "service"
                           ? "bg-[#EAF4EE] text-[#2F6B4A]"
                           : "bg-[#F2ECE5] text-[#5B4B2E]"
                       }`}>
                  {form.kind === "service" ? "Service" : "Inventory"}
                </span>
                {form.on_hold && (
                  <span data-testid="invoice-onhold-badge"
                         className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FCE7E9] text-[#B4283A]">
                    On hold
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            {/* Row 1 — dates + currency */}
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setDate(e.target.value)}
                        data-testid="invoice-form-date" />
              </div>
              <div><Label>GL posting date</Label>
                <Input type="date" value={form.gl_posting_date || ""}
                        onChange={(e) => setForm({ ...form, gl_posting_date: e.target.value })}
                        data-testid="invoice-form-gl-date" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency || "USD"} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="invoice-form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="NGN">NGN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Row 2 — customer + numbers */}
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <Label>Customer *</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="invoice-form-customer"><SelectValue placeholder="Pick customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" size="sm" variant="outline" className="mt-1 h-7 text-xs"
                         data-testid="invoice-populate-last"
                         disabled={!form.customer_id || populating}
                         onClick={populateFromLast}>
                  {populating ? "Populating…" : "Populate from last invoice"}
                </Button>
              </div>
              <div>
                <Label>Invoice number</Label>
                <Input placeholder="Auto-generated"
                        value={form.number || ""}
                        onChange={(e) => setForm({ ...form, number: e.target.value })}
                        data-testid="invoice-form-number" />
              </div>
              <div>
                <Label>Reference number</Label>
                <Input placeholder="Customer PO # / ext ref"
                        value={form.reference_number || ""}
                        onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                        data-testid="invoice-form-ref" />
              </div>
            </div>
            {/* Row 3 — description */}
            <div className="mt-3">
              <Label>Description</Label>
              <Input placeholder="Short header description"
                      value={form.description || ""}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      data-testid="invoice-form-description" />
            </div>
            {/* Row 4 — terms + due + priority + hold */}
            <div className="grid grid-cols-4 gap-3 mt-3 items-end">
              <div>
                <Label>Payment terms</Label>
                <Select value={form.terms || "custom"} onValueChange={setTerms}>
                  <SelectTrigger data-testid="invoice-form-terms"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="due_on_receipt">Due on receipt</SelectItem>
                    <SelectItem value="net10">Net 10</SelectItem>
                    <SelectItem value="net15">Net 15</SelectItem>
                    <SelectItem value="net30">Net 30</SelectItem>
                    <SelectItem value="net45">Net 45</SelectItem>
                    <SelectItem value="net60">Net 60</SelectItem>
                    <SelectItem value="net90">Net 90</SelectItem>
                    <SelectItem value="eom">End of month</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due date *</Label>
                <Input type="date" value={form.due_date}
                        onChange={(e) => setForm({ ...form, due_date: e.target.value, terms: "custom" })}
                        data-testid="invoice-form-due" />
              </div>
              <div>
                <Label>Payment priority</Label>
                <Select value={form.payment_priority || "normal"}
                         onValueChange={(v) => setForm({ ...form, payment_priority: v })}>
                  <SelectTrigger data-testid="invoice-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 h-9">
                <input type="checkbox" checked={!!form.on_hold}
                        onChange={(e) => setForm({ ...form, on_hold: e.target.checked })}
                        id="invoice-hold" data-testid="invoice-form-hold"
                        className="h-4 w-4" />
                <label htmlFor="invoice-hold" className="text-sm">Place this invoice on hold</label>
              </div>
            </div>
            {/* Attachment placeholder */}
            <div className="mt-3 text-xs text-[var(--muted-text)] italic"
                  data-testid="invoice-form-attachment-placeholder">
              Attachment: coming soon (needs cloud file storage setup)
            </div>

            <div className="mt-4 border-t border-[var(--divider)] pt-3">
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] mb-2">Entries</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                  {form.kind !== "service" && (<th className="py-2 text-left w-40">Product</th>)}
                  <th className="py-2 text-left">Description</th>
                  <th className="py-2 w-16 text-right">Qty</th>
                  <th className="py-2 w-20 text-right">Unit</th>
                  <th className="py-2 w-14 text-right">Tax%</th>
                  <th className="py-2 text-left w-36">Memo</th>
                  <th className="py-2 w-24 text-right">Line</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => {
                  const line = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
                  const total = line + line * (parseFloat(l.tax_rate) || 0) / 100;
                  return (
                    <Fragment key={l._rid}>
                    <tr className="border-b border-[var(--divider)]">
                      {form.kind !== "service" && (
                      <td className="py-2 pr-2">
                        <Select value={l.product_id || "none"} onValueChange={(v) => pickProduct(i, v === "none" ? "" : v)}>
                          <SelectTrigger className="h-9 text-xs" data-testid={`invoice-line-${i}-product`}>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— none —</SelectItem>
                            {products.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name}{p.sku ? ` · ${p.sku}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      )}
                      <td className="py-2 pr-2"><Input value={l.description} onChange={(e) => { const n = [...form.lines]; n[i].description = e.target.value; setForm({ ...form, lines: n }); }} className="h-9" placeholder="Item / service" data-testid={`invoice-line-${i}-desc`} /></td>
                      <td className="py-2 pr-2"><Input type="number" value={l.quantity} onChange={(e) => { const n = [...form.lines]; n[i].quantity = e.target.value; setForm({ ...form, lines: n }); }} className="h-9 text-right" /></td>
                      <td className="py-2 pr-2"><Input type="number" step="0.01" value={l.unit_price} onChange={(e) => { const n = [...form.lines]; n[i].unit_price = e.target.value; setForm({ ...form, lines: n }); }} className="h-9 text-right" /></td>
                      <td className="py-2 pr-2"><Input type="number" step="0.01" value={l.tax_rate} onChange={(e) => { const n = [...form.lines]; n[i].tax_rate = e.target.value; setForm({ ...form, lines: n }); }} className="h-9 text-right" /></td>
                      <td className="py-2 pr-2"><Input placeholder="Line memo" value={l.memo || ""} onChange={(e) => { const n = [...form.lines]; n[i].memo = e.target.value; setForm({ ...form, lines: n }); }} className="h-9" data-testid={`invoice-line-${i}-memo`} /></td>
                      <td className="py-2 pr-2 text-right font-mono">{total.toFixed(2)}</td>
                      <td>{form.lines.length > 1 && <Button size="icon" variant="ghost" onClick={() => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) })}><X className="w-4 h-4" /></Button>}</td>
                    </tr>
                    {/* iter 72ah — per-line accounting tags: CoA account,
                        Department, and target Bank (for later settlement).
                        Only the Revenue accounts appear in the CoA picker. */}
                    <tr className="border-b border-[var(--divider)] bg-[var(--hover)]/40" data-testid={`invoice-line-${i}-tags`}>
                      <td colSpan={form.kind === "service" ? 7 : 8} className="pt-1 pb-2 px-2">
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] mb-1">CoA account (Revenue)</div>
                            <Select value={l.coa_code || "none"}
                                     onValueChange={(v) => { const n = [...form.lines]; n[i].coa_code = v === "none" ? "" : v; setForm({ ...form, lines: n }); }}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`invoice-line-${i}-coa`}>
                                <SelectValue placeholder="— default (4000 Sales) —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— default (4000 Sales) —</SelectItem>
                                {coaRevenue.map((a) => (
                                  <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] mb-1">Department</div>
                            <Select value={l.department || "none"}
                                     onValueChange={(v) => { const n = [...form.lines]; n[i].department = v === "none" ? "" : v; setForm({ ...form, lines: n }); }}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`invoice-line-${i}-dept`}>
                                <SelectValue placeholder="— none —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— none —</SelectItem>
                                {departments.map((d) => (
                                  <SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] mb-1">Settle to bank (later)</div>
                            <Select value={l.bank_account_id || "none"}
                                     onValueChange={(v) => { const n = [...form.lines]; n[i].bank_account_id = v === "none" ? "" : v; setForm({ ...form, lines: n }); }}>
                              <SelectTrigger className="h-8 text-xs" data-testid={`invoice-line-${i}-bank`}>
                                <SelectValue placeholder="— pick later —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— pick later —</SelectItem>
                                {wallets.filter((w) => (w.currency || "USD") === (form.currency || "USD")).map((w) => (
                                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </td>
                    </tr>
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr><td colSpan={form.kind === "service" ? 7 : 8}><Button size="sm" variant="ghost" onClick={() => setForm({ ...form, lines: [...form.lines, withRid({ product_id: "", description: "", quantity: 1, unit_price: 0, tax_rate: 0, coa_code: "", department: "", bank_account_id: "", memo: "" })] })}><Plus className="w-3.5 h-3.5 mr-1" /> Add line</Button></td></tr>
                <tr><td colSpan={form.kind === "service" ? 5 : 6} className="pt-2 text-right text-xs text-[var(--muted-text)]">Subtotal</td><td className="text-right font-mono">{totals.subtotal.toFixed(2)}</td><td /></tr>
                <tr><td colSpan={form.kind === "service" ? 5 : 6} className="text-right text-xs text-[var(--muted-text)]">Tax</td><td className="text-right font-mono">{totals.tax_total.toFixed(2)}</td><td /></tr>
                <tr><td colSpan={form.kind === "service" ? 5 : 6} className="text-right text-sm font-semibold">Total</td><td data-testid="invoice-total" className="text-right font-mono font-semibold border-t border-[#2F4F4F]">{totals.total.toFixed(2)}</td><td /></tr>
              </tfoot>
            </table>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button variant="outline" data-testid="invoice-form-post-and-new"
                       onClick={() => save({ postAndNew: true })}
                       className="rounded-full">
                Save & new
              </Button>
              <Button data-testid="invoice-form-save" onClick={() => save()} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">{editing ? "Save" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-hidden">
        {(() => { const visible = invoices.filter((inv) => isActive(inv.currency || "USD")); return visible.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">
            {invoices.length === 0 ? "No invoices yet." : "No invoices on the selected currency rail."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
              <tr><th className="text-left px-5 py-2.5">Number</th><th className="text-left px-4 py-2.5">Customer</th><th className="text-left px-4 py-2.5">Due</th><th className="text-left px-4 py-2.5">Ccy</th><th className="text-right px-4 py-2.5">Total</th><th className="text-right px-4 py-2.5">Balance</th><th className="px-4 py-2.5">Status</th><th className="w-32" /></tr>
            </thead>
            <tbody>
              {visible.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b border-[var(--divider)] last:border-none">
                  <td className="px-5 py-3 font-mono text-xs">{inv.number}</td>
                  <td className="px-4 py-3">{inv.customer_name}</td>
                  <td className="px-4 py-3 text-[var(--muted-text)]">{inv.due_date}</td>
                  <td className="px-4 py-3 font-mono text-[10px]"><span className="px-1.5 py-0.5 rounded bg-[var(--hover)]">{inv.currency || "USD"}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{formatMoney(inv.total, inv.currency)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatMoney(inv.balance, inv.currency)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${STATUS_STYLES[inv.status]}`}>{inv.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    {inv.status !== "paid" && (
                      <Button size="icon" variant="ghost" data-testid={`invoice-pay-${inv.id}`}
                               onClick={() => {
                                 // iter 72ai — pre-fill the payment source from the first
                                 // line that carries a `bank_account_id`. One click = done.
                                 const firstBank = (inv.lines || []).find((l) => l.bank_account_id)?.bank_account_id;
                                 setPay({
                                   amount: String(inv.balance),
                                   date: format(new Date(), "yyyy-MM-dd"),
                                   method: firstBank ? "bank" : "bank",
                                   memo: "",
                                   account_id: firstBank || "",
                                 });
                                 setPayOpen(inv);
                               }}
                               title="Record payment">
                        <CreditCard className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" data-testid={`invoice-post-${inv.id}`} title={inv.posted_journal_id ? "Already posted — click Reverse to unpost" : "Post to journal"} disabled={!!inv.posted_journal_id} onClick={async () => {
                      try {
                        const { data } = await api.post(`/invoices/${inv.id}/post-journal`);
                        toast.success(data.already_posted ? "Already posted to journal" : "Journal entry posted");
                        load();
                      } catch (err) { toast.error(err.response?.data?.detail || "Post failed"); }
                    }}>
                      <BookOpenCheck className="w-4 h-4" />
                    </Button>
                    {inv.posted_journal_id && (
                      <Button size="icon" variant="ghost" data-testid={`invoice-reverse-${inv.id}`} title="Reverse posted journal entry" onClick={() => setReverseTarget(inv)}>
                        <Undo2 className="w-4 h-4 text-[#B4283A]" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" data-testid={`invoice-paylink-${inv.id}`} title="Copy public pay-now link" onClick={async () => {
                      try {
                        const { data } = await api.post(`/paypal/invoices/${inv.id}/public-token`);
                        const url = `${window.location.origin}/pay/${data.token}`;
                        try { await navigator.clipboard.writeText(url); toast.success("Pay link copied to clipboard"); }
                        catch { toast.info(url); }
                      } catch (err) { toast.error(err.response?.data?.detail || "Couldn't generate link"); }
                    }}>
                      <Link2 className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" data-testid={`invoice-paylink-email-${inv.id}`} title="Email pay link to customer" onClick={async () => {
                      if (!window.confirm("Email the pay link to this customer? They'll get a branded email with a 'Pay this invoice' button.")) return;
                      try {
                        const { data } = await api.post(`/paypal/invoices/${inv.id}/email-pay-link`, {});
                        toast.success(`Pay link sent to ${data.sent_to}`);
                      } catch (err) { toast.error(err.response?.data?.detail || "Couldn't email pay link"); }
                    }}>
                      <Mail className="w-4 h-4 text-[#4C956C]" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(inv)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(inv.id)}><Trash2 className="w-4 h-4 text-[#E63946]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ); })()}
      </div>

      <Dialog open={!!payOpen} onOpenChange={(v) => !v && setPayOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record payment</DialogTitle><DialogDescription>Invoice {payOpen?.number} · balance {formatMoney(payOpen?.balance, payOpen?.currency)}</DialogDescription></DialogHeader>

          {/* Iter 72 — existing payments with void action */}
          {(payOpen?.payments || []).length > 0 && (
            <div className="border border-[var(--divider)] rounded-md" data-testid="invoice-payments-list">
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[var(--muted-text)] border-b border-[var(--divider)]">
                Recorded payments
              </div>
              <ul className="divide-y divide-[var(--divider)]">
                {(payOpen.payments || []).map((p) => (
                  <li key={p.id} className="flex items-center justify-between px-3 py-2 text-sm"
                      data-testid={`invoice-payment-row-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-data">{formatMoney(p.amount, payOpen.currency)} <span className="text-[var(--muted-text)] text-xs">· {p.date} · {p.method}</span></div>
                      {p.memo && <div className="text-xs text-[var(--muted-text)] truncate">{p.memo}</div>}
                    </div>
                    {p.method === "credit_memo" ? (
                      <span className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] px-2">memo</span>
                    ) : (
                      <Button size="sm" variant="ghost"
                        data-testid={`invoice-payment-void-${p.id}`}
                        className="text-[#B4283A] hover:bg-[#FCE7E9]"
                        onClick={() => { setVoidTarget({ paymentId: p.id, amount: p.amount, date: p.date }); setVoidReason(""); }}>
                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Reverse payment
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
              {(payOpen?.voided_payments || []).length > 0 && (
                <div className="px-3 py-2 border-t border-[var(--divider)] bg-[var(--hover)]">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">
                    Voided ({payOpen.voided_payments.length})
                  </div>
                  <ul className="space-y-1">
                    {payOpen.voided_payments.map((p) => (
                      <li key={p.id} className="text-xs text-[var(--muted-text)] line-through"
                          data-testid={`invoice-payment-voided-${p.id}`}>
                        {formatMoney(p.amount, payOpen.currency)} · {p.date} · {p.voided_reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount</Label><Input type="number" step="0.01" data-testid="payment-amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
            <div><Label>Date</Label><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Method</Label>
              <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank transfer</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* iter 72ai — Bank / wallet the payment lands into. Pre-filled
                from the invoice line's `bank_account_id` when set, but
                fully editable. */}
            <div className="col-span-2">
              <Label>Deposit to bank {pay.account_id && <span className="text-[10px] text-[#2F6B4A] ml-2">(auto-filled from line)</span>}</Label>
              <Select value={pay.account_id || "none"}
                       onValueChange={(v) => setPay({ ...pay, account_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="payment-bank"><SelectValue placeholder="— none —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {wallets.filter((w) => (w.currency || "USD") === (payOpen?.currency || "USD")).map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Memo</Label><Input value={pay.memo} onChange={(e) => setPay({ ...pay, memo: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setPayOpen(null)}>Close</Button>
            <Button data-testid="payment-save" onClick={recordPayment} className="rounded-full bg-[#2F6B4A] hover:bg-[#245536] text-white">Record</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter 72 — Void payment (aka Reverse Payment) confirmation with required reason */}
      <Dialog open={!!voidTarget} onOpenChange={(v) => { if (!v) { setVoidTarget(null); setVoidReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="invoice-payment-void-dialog">
          <DialogHeader>
            <DialogTitle>Reverse payment</DialogTitle>
            <DialogDescription>
              Reversing {voidTarget && formatMoney(voidTarget.amount, payOpen?.currency)} recorded on {voidTarget?.date} will undo its cash impact and keep an audit record on this invoice. This action is preserved in the audit log — it is not a delete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Reason <span className="text-[#B4283A]">*</span></Label>
            <Textarea data-testid="invoice-payment-void-reason" value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Bounced check / duplicate posting / chargeback"
              rows={3} />
            <p className="text-xs text-[var(--muted-text)]">Minimum 3 characters. Stored on the invoice audit trail.</p>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="ghost" onClick={() => { setVoidTarget(null); setVoidReason(""); }}>Cancel</Button>
            <Button data-testid="invoice-payment-void-confirm"
              className="bg-[#B4283A] hover:bg-[#8f1f2d] text-white" onClick={confirmVoid}>
              Reverse payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reverseTarget} onOpenChange={(v) => { if (!v) { setReverseTarget(null); setReverseReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="invoice-reverse-dialog">
          <DialogHeader>
            <DialogTitle>Reverse journal entry</DialogTitle>
            <DialogDescription>
              This will post an offsetting journal entry for invoice {reverseTarget?.number} ({formatMoney(reverseTarget?.total)}) and flip it back to <span className="font-semibold">unposted</span> so it can be re-posted after correction. The original journal entry remains in the ledger for audit — this is not a delete.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Reason (optional but recommended)</Label>
            <Textarea data-testid="invoice-reverse-reason" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} placeholder="e.g. Wrong customer / duplicate posting / amount corrected" rows={3} />
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="ghost" onClick={() => { setReverseTarget(null); setReverseReason(""); }}>Cancel</Button>
            <Button data-testid="invoice-reverse-confirm" className="bg-[#B4283A] hover:bg-[#8f1f2d] text-white" onClick={async () => {
              try {
                const { data } = await api.post(`/invoices/${reverseTarget.id}/reverse-journal`, { reason: reverseReason });
                toast.success(`Reversed · offsetting JE created (${data.reversal_je_id.slice(-6)})`);
                setReverseTarget(null); setReverseReason("");
                load();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Reversal failed");
              }
            }}>Reverse posting</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Aging Tab ----------------
